import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Skeleton, 
  AspectRatio, 
  Icon, 
  Text, 
  Center, 
  Tag, 
  HStack, 
  Wrap, 
  WrapItem,
  Badge,
  useColorModeValue,
  IconButton,
  Flex,
  Image as ChakraImage
} from '@chakra-ui/react';
import { Image as ImageIcon, MagicWand, Camera, CaretLeft, CaretRight, User, ListPlus } from '@phosphor-icons/react';
import { LazyImage } from '../../utils/lazyLoadUtils';
import { normalizeFeature, mapFeature } from '../../utils/featureUtils';

/**
 * A component that selects and displays up to 5 relevant images from a site's images in a carousel,
 * prioritizing images that match active filters.
 */
const SearchBetaImagePreview = ({ 
  images, 
  activeFeatures = [], 
  onImageClick,
  alt = "Site image"
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filteredImages, setFilteredImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFeatures, setCurrentFeatures] = useState([]);
  const [preloadedURLs, setPreloadedURLs] = useState(new Set());
  
  // Create refs to hold preloaded image elements
  const preloadedImagesRef = useRef({});
  const isFirstRender = useRef(true);
  
  // Colors for feature tags and carousel controls
  const tagBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(0, 0, 0, 0.75)');
  const tagColor = useColorModeValue('ancient.terracotta.800', 'ancient.terracotta.100');
  const matchedTagBg = useColorModeValue('ancient.terracotta.100', 'ancient.terracotta.900');
  const matchedTagColor = useColorModeValue('ancient.terracotta.800', 'white');
  const arrowBg = useColorModeValue('rgba(255, 255, 255, 0.7)', 'rgba(0, 0, 0, 0.5)');
  const arrowHoverBg = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(0, 0, 0, 0.7)');
  
  // Format feature name for display
  const formatFeatureName = (feature) => {
    if (!feature) return '';
    
    // Add spaces before capital letters
    return feature
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };
  
  // Get match count and features for an image
  const getImageMatchData = useCallback((image, normalizedActiveFeatures) => {
    // Get predictions from the image
    const predictions = image.predictions || [];
    
    // Track matched features with additional metadata
    const matchedFeaturesList = [];
    const featuresForDisplay = [];
    
    // Check each prediction against active features
    predictions.forEach(prediction => {
      // Skip low confidence auto predictions
      if (prediction.source === 'auto' && prediction.confidence < 0.05) return;
      
      // Use mapFeature for consistent handling
      const { featureId, label } = mapFeature(prediction);
      if (!featureId) return;
      
      const predFeatureId = normalizeFeature(featureId);
      
      // Check if this prediction matches any active feature
      const matchingActiveFeature = normalizedActiveFeatures.find(
        activeFeature => predFeatureId === activeFeature
      );
      
      if (matchingActiveFeature) {
        matchedFeaturesList.push(matchingActiveFeature);
        // Store more complete feature information
        featuresForDisplay.push({
          id: featureId,
          class: prediction.class, 
          label: label || prediction.class,
          source: prediction.source || 'auto',
          confidence: prediction.confidence || 0
        });
      }
    });
    
    return { 
      image, 
      matchCount: matchedFeaturesList.length,
      matchedFeatures: featuresForDisplay
    };
  }, []);
  
  // Select up to 5 most relevant images based on active features
  useEffect(() => {
    if (!images || images.length === 0) {
      setFilteredImages([]);
      setCurrentFeatures([]);
      return;
    }
    
    // Always show at least some images, regardless of filter matches
    // We'll prefer images with matching features, but won't hide all images if none match
    
    // Normalize active features for comparison
    const normalizedActiveFeatures = activeFeatures.map(f => normalizeFeature(f));
    
    // If no filters are active, just show the first 5 images
    if (!activeFeatures || activeFeatures.length === 0) {
      const imagesToShow = images.slice(0, 5);
      setFilteredImages(imagesToShow);
      setCurrentIndex(0);
      setCurrentFeatures([]);
      return;
    }
    
    // Calculate match data for all images to find the best ones
    const imageMatchData = images.map(image => 
      getImageMatchData(image, normalizedActiveFeatures)
    );
    
    // Sort by match count (highest first)
    imageMatchData.sort((a, b) => b.matchCount - a.matchCount);
    
    let imagesToShow = [];
    let featuresForFirstImage = [];
    
    // First try to get images with matches
    const bestMatches = imageMatchData
      .filter(match => match.matchCount > 0)
      .slice(0, 5);
    
    if (bestMatches.length > 0) {
      // We found images with matching features - use them
      imagesToShow = bestMatches.map(match => match.image);
      featuresForFirstImage = bestMatches[0].matchedFeatures;
    } else {
      // No image directly matches the features, but site is in results
      // Show the first 5 images anyway (the site as a whole matches)
      imagesToShow = images.slice(0, 5);
      featuresForFirstImage = [];
      
      // For the first image, try to inject site-level feature data even if image doesn't have matches
      // This would make features visible in the image even if they aren't directly in the image predictions
      if (imagesToShow.length > 0 && normalizedActiveFeatures.length > 0) {
        // Create synthetic feature data from the active features
        const syntheticFeatures = normalizedActiveFeatures.map(featureId => ({
          id: featureId,
          class: featureId,
          label: formatFeatureName(featureId),
          source: 'system', // Indicate this is from site-level data
          confidence: 1.0 // High confidence as this is from site-level data
        }));
        
        // Limit to first 3 to avoid overcrowding
        featuresForFirstImage = syntheticFeatures.slice(0, 3);
      }
    }
    
    console.log('[SearchBetaImagePreview] Images to Show:', imagesToShow); // <-- ADDED LOG
    console.log('[SearchBetaImagePreview] Features for First Image:', featuresForFirstImage); // <-- ADDED LOG
    
    setFilteredImages(imagesToShow);
    setCurrentIndex(0);
    setCurrentFeatures(featuresForFirstImage);
    
  }, [images, activeFeatures, getImageMatchData]);
  
  // Safer preload method using hidden Chakra images
  const preloadImage = useCallback((url) => {
    if (!url || preloadedURLs.has(url)) return;
    
    // Create a hidden image element to preload the image
    if (!preloadedImagesRef.current[url]) {
      const imgRef = document.createElement('link');
      imgRef.rel = 'preload';
      imgRef.as = 'image';
      imgRef.href = url;
      
      document.head.appendChild(imgRef);
      
      // Store reference so we can clean up later
      preloadedImagesRef.current[url] = imgRef;
      
      // Update preloaded URLs set
      setPreloadedURLs(prev => {
        const newSet = new Set(prev);
        newSet.add(url);
        return newSet;
      });
    }
  }, [preloadedURLs]);
  
  // Preload adjacent images
  useEffect(() => {
    if (!filteredImages.length) return;
    
    // If this is the first render, don't preload yet
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Preload next and previous images (if they exist)
    if (filteredImages.length > 1) {
      const nextIndex = (currentIndex + 1) % filteredImages.length;
      const nextUrl = filteredImages[nextIndex]?.url;
      if (nextUrl) {
        preloadImage(nextUrl);
      }
      
      // Also preload previous image
      const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
      const prevUrl = filteredImages[prevIndex]?.url;
      if (prevUrl) {
        preloadImage(prevUrl);
      }
    }
  }, [filteredImages, currentIndex, preloadImage]);
  
  // When current index changes, update the current features
  useEffect(() => {
    if (!filteredImages.length || currentIndex >= filteredImages.length) return;
    
    const currentImage = filteredImages[currentIndex];
    
    if (!activeFeatures || activeFeatures.length === 0) {
      setCurrentFeatures([]);
      return;
    }
    
    const normalizedActiveFeatures = activeFeatures.map(f => normalizeFeature(f));
    const { matchedFeatures } = getImageMatchData(currentImage, normalizedActiveFeatures);
    // Only show features that are directly matched within the current image
    // Remove the fallback logic for synthetic/site-level features
    if (matchedFeatures.length > 0) {
      setCurrentFeatures(matchedFeatures);
    } else {
      // If the current image has no matching features, show no feature tags
      setCurrentFeatures([]);
    }
  }, [currentIndex, filteredImages, activeFeatures, getImageMatchData]);
  
  // Clean up preloaded image refs on unmount
  useEffect(() => {
    return () => {
      // Remove all preload links
      Object.values(preloadedImagesRef.current).forEach(linkElement => {
        if (linkElement && linkElement.parentNode) {
          linkElement.parentNode.removeChild(linkElement);
        }
      });
    };
  }, []);
  
  // Navigation functions
  const goToPrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
  };
  
  // If no images are available, show a placeholder
  if (!filteredImages.length) {
    return (
      <AspectRatio ratio={16/9} width="100%" height="100%">
        <Center bg="gray.200" borderRadius="md">
          <Icon as={ImageIcon} boxSize={8} color="gray.500" />
        </Center>
      </AspectRatio>
    );
  }
  
  const currentImage = filteredImages[currentIndex];
  
  // Limit displayed features to 3 to avoid overcrowding
  const displayFeatures = currentFeatures.slice(0, 3);
  const hasMoreFeatures = currentFeatures.length > 3;
  
  return (
    <Box position="relative" width="100%" height="100%" overflow="hidden">
      <AspectRatio ratio={16/9} width="100%">
        <Skeleton isLoaded={!isLoading} height="100%" width="100%" borderRadius={0}>
          {/* Debug information for current image */}
          {console.log('Current image:', currentImage)}
          {currentImage?.url ? (
            <Box 
              position="relative" 
              width="100%" 
              height="100%"
              overflow="hidden"
              _hover={{
                '& > img': {
                  transform: 'scale(1.05)',
                }
              }}
            >
              <img
                src={currentImage.url}
                alt={alt}
                style={{
                  objectFit: "cover", 
                  height: "100%",
                  width: "100%",
                  cursor: "pointer",
                  transition: "transform 0.3s ease"
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Stop the click from bubbling up to the card
                  onImageClick && onImageClick(currentImage);
                }}
                onLoad={() => setIsLoading(false)}
              />
            </Box>
          ) : (
            <Box bg="gray.700" display="flex" alignItems="center" justifyContent="center">
              <Text color="white">Image Not Available</Text>
            </Box>
          )}
        </Skeleton>
      </AspectRatio>
      
      {/* Feature Tag Overlays */}
      {!isLoading && displayFeatures.length > 0 && (
        <Box 
          position="absolute" 
          bottom="0"
          left="0"
          right="0"
          p={2}
          bg="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.4), transparent)"
          zIndex={2}
        >
          <Wrap spacing={1}>
            {displayFeatures.map((feature, index) => {
              // Determine which icon to use based on source
              const FeatureIcon = feature.source === 'auto' ? MagicWand : 
                                 feature.source === 'manual' ? User : ListPlus;
              
              // Display a checkmark for high confidence features (>= 75%)
              const hasHighConfidence = feature.source === 'auto' && feature.confidence >= 0.75;
              
              // Feature name to display - use label if available, otherwise format the ID
              const displayName = feature.label || formatFeatureName(feature.id || feature.class);
              
              return (
                <WrapItem key={index}>
                  <Tag
                    size="sm"
                    bg={matchedTagBg}
                    color={matchedTagColor}
                    borderRadius="full"
                    fontWeight="medium"
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    <Icon as={FeatureIcon} mr={1} boxSize="10px" />
                    {displayName}
                    {hasHighConfidence && " ✓"}
                  </Tag>
                </WrapItem>
              );
            })}
            {hasMoreFeatures && (
              <WrapItem>
                <Tag
                  size="sm"
                  bg={tagBg}
                  color={tagColor}
                  borderRadius="full"
                  fontWeight="medium"
                  px={2}
                  py={1}
                  fontSize="xs"
                >
                  +{currentFeatures.length - 3} more
                </Tag>
              </WrapItem>
            )}
          </Wrap>
        </Box>
      )}
      
      {/* Image Count Badge */}
      {!isLoading && (
        <Box 
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
        >
          <Badge
            bg={tagBg}
            color={tagColor}
            borderRadius="full"
            px={2}
            py={1}
            fontSize="xs"
            display="flex"
            alignItems="center"
          >
            <Icon as={Camera} mr={1} boxSize="12px" />
            {currentIndex + 1}/{filteredImages.length}
          </Badge>
        </Box>
      )}
      
      {/* Navigation Arrows - Only show if there are multiple images */}
      {filteredImages.length > 1 && !isLoading && (
        <>
          <IconButton
            icon={<Icon as={CaretLeft} boxSize={4} />}
            onClick={goToPrev}
            position="absolute"
            left={1}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            size="sm"
            borderRadius="full"
            bg={arrowBg}
            color="ancient.terracotta"
            _hover={{ bg: arrowHoverBg }}
            aria-label="Previous image"
            variant="ghost"
            isRound
          />
          <IconButton
            icon={<Icon as={CaretRight} boxSize={4} />}
            onClick={goToNext}
            position="absolute"
            right={1}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            size="sm"
            borderRadius="full"
            bg={arrowBg}
            color="ancient.terracotta"
            _hover={{ bg: arrowHoverBg }}
            aria-label="Next image"
            variant="ghost"
            isRound
          />
          
          {/* Navigation Dots */}
          <Flex 
            position="absolute" 
            bottom={1} 
            left="50%" 
            transform="translateX(-50%)" 
            zIndex={2}
            gap={1}
          >
            {filteredImages.map((_, index) => (
              <Box
                key={index}
                w={currentIndex === index ? "8px" : "6px"}
                h={currentIndex === index ? "8px" : "6px"}
                borderRadius="full"
                bg={currentIndex === index ? "white" : "whiteAlpha.600"}
                transition="all 0.2s"
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log(`[SearchBetaImagePreview] Dot clicked, setting index to: ${index}`);
                  setCurrentIndex(index);
                }}
              />
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
};

export default SearchBetaImagePreview;
